/**
 * VaultLayout — the persistent chrome (sidebar + main) for the routed Document
 * Vault. It renders the brand block, the Workspace/Administration navigation as
 * router `<Link>`s, and the signed-in user chip, wrapping the active route's
 * content in `<main>`.
 *
 * This replaces the sidebar/shell that previously lived inline in
 * `DocumentVault` (`index.tsx`). It is purely presentational: the only data it
 * reads is the current user (for the chip) via `useVaultData()`.
 *
 * Note: `react-router-dom` here is v5, so we use `Link` + `useLocation` (not the
 * v6 `useNavigate`/`<Outlet>`).
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@jsq/ds/icons";
import * as styles from "./styles_vanilla.css";
import { initials } from "./utils";
import { useVaultData } from "./VaultProvider";
import type { DsIconType } from "#common/icons";

/**
 * The Sharing section is switched OFF: nothing behind it is backed (collections,
 * share log, and channels are all presentational), so the nav entry is not
 * rendered. Deliberately NOT wired to the `documentVaultM2` flag (DV-646) —
 * unlike client scoping, there is no backend to flip on here yet, so this stays
 * a hardcoded constant rather than something a flag could expose by mistake.
 * `SharingRoute` is still exported, so a host that wires the route keeps a
 * working deep link — this only removes the way in. Flip to restore the entry.
 */
const SHARING_ENABLED = false;

type LayoutNavItem = {
  label: string;
  icon: DsIconType;
  /** The `<Link>` destination. */
  to: string;
  /** Section base used for active-state matching (the item is active when the
   * current pathname equals it or is nested under it). */
  base: string;
};

export const VaultLayout = ({
  basePath,
  children,
}: {
  basePath: string;
  children: React.ReactNode;
}) => {
  const { user, loading, loadingFallback } = useVaultData();
  const { pathname } = useLocation();

  const workspaceNav: LayoutNavItem[] = [
    {
      label: "Documents",
      icon: "file-alt",
      to: `${basePath}/documents`,
      base: `${basePath}/documents`,
    },
    // Sharing is switched off — see SHARING_ENABLED.
    ...(SHARING_ENABLED
      ? [
          {
            label: "Sharing",
            icon: "share" as const,
            to: `${basePath}/sharing`,
            base: `${basePath}/sharing`,
          },
        ]
      : []),
  ];
  const adminNav: LayoutNavItem[] = [
    {
      label: "Settings",
      icon: "cog",
      to: `${basePath}/settings/spaces`,
      base: `${basePath}/settings`,
    },
  ];

  const roleLabel = user.isSteward ? "Global-attribute steward" : user.role;

  const renderNavItem = (item: LayoutNavItem) => {
    const active = pathname === item.base || pathname.startsWith(`${item.base}/`);
    return (
      <Link
        key={item.to}
        to={item.to}
        className={active ? styles.sbItem.active : styles.sbItem.inactive}
        aria-current={active ? "page" : undefined}
      >
        <span className={styles.sbItemIcon}>
          <Icon type={item.icon} size="medium" customColor={active ? "#67ccce" : "#908f8a"} />
        </span>
        {item.label}
      </Link>
    );
  };

  return (
    <div className={styles.app}>
      <nav className={styles.sidebar} aria-label="Vault sections">
        <div className={styles.sbBrand}>
          <div className={styles.sbMark}>
            <Icon type="cabinet-filing" size="medium" customColor="#ffffff" />
          </div>
          <div>
            <div className={styles.brandName}>Document Vault</div>
            <div className={styles.brandSub}>Juniper Square</div>
          </div>
        </div>

        <div className={styles.sbLabel}>Workspace</div>
        <div className={styles.sbNav}>{workspaceNav.map(renderNavItem)}</div>

        <div className={styles.sbAdmin}>
          <div className={styles.sbLabel}>Administration</div>
          <div className={styles.sbNav}>{adminNav.map(renderNavItem)}</div>
        </div>

        <div className={styles.sbFoot}>
          <div className={styles.userchip}>
            <span className={styles.ucAvatar}>{initials(user.name)}</span>
            <div className={styles.ucText}>
              <span className={styles.ucName}>{user.name}</span>
              <span className={styles.ucRole}>{roleLabel}</span>
            </div>
          </div>
        </div>
      </nav>

      {/* The loader lives HERE, not above the layout: the sidebar stays visible and
          clickable while a route's shared data loads. */}
      <main className={styles.main}>{loading ? loadingFallback : children}</main>
    </div>
  );
};
